import { useMemo, useState } from 'react';
import { ethers } from 'ethers';
import { useWallet } from '../../context/WalletContext';
import { useContract } from '../../context/ContractContext';
import { useNotification } from '../../context/NotificationContext';
import { readProvider } from '../../config/provider';
import {
  ACTIVE_CONTRACTS,
  LOGIC_ADMIN_ABI,
  VIEWS_ABI,
  STORAGE_ADMIN_ABI,
  ROUTER_LOGIC_ABI,
  ROUTER_STORAGE_ABI
} from '../../config/contract';

import { parseContractError } from '../../utils/contractErrors';

// UI helpers: percent <-> basis points, USDT <-> wei
export const pctToBps = (pct) => Math.round(parseFloat(pct) * 100);
export const bpsToPct = (bps) => (Number(bps.toString()) / 100).toString();
export const toWei = (v) => ethers.utils.parseEther(String(v));
export const fromWei = (v) => ethers.utils.formatEther(v);

export const useAdmin = () => {
  const { signer } = useWallet();
  const { isOwner, isAdmin, checkRegistration } = useContract();
  const { success, error: showError } = useNotification();
  const [pending, setPending] = useState(null);

  const read = useMemo(() => ({
    views: new ethers.Contract(ACTIVE_CONTRACTS.views, VIEWS_ABI, readProvider),
    storage: new ethers.Contract(ACTIVE_CONTRACTS.storage, STORAGE_ADMIN_ABI, readProvider)
  }), []);

  const write = useMemo(() => {
    if (!signer) return null;
    return {
      logic: new ethers.Contract(ACTIVE_CONTRACTS.logic, LOGIC_ADMIN_ABI, signer),
      storage: new ethers.Contract(ACTIVE_CONTRACTS.storage, STORAGE_ADMIN_ABI, signer)
    };
  }, [signer]);

  // Router pool contracts exist ONLY for the owner
  const router = useMemo(() => {
    if (!isOwner) return null;
    return {
      readStorage: new ethers.Contract(ACTIVE_CONTRACTS.storage, ROUTER_STORAGE_ABI, readProvider),
      logic: signer ? new ethers.Contract(ACTIVE_CONTRACTS.logic, ROUTER_LOGIC_ABI, signer) : null,
      storage: signer ? new ethers.Contract(ACTIVE_CONTRACTS.storage, ROUTER_STORAGE_ABI, signer) : null
    };
  }, [isOwner, signer]);

  // Send a tx, wait for it, toast the result. `key` drives button loading state.
  const runTx = async (key, label, send) => {
    if (!write) {
      showError('Please connect your wallet');
      return false;
    }
    setPending(key);
    try {
      const tx = await send(write);
      await tx.wait();
      success(`${label} successful`);
      return true;
    } catch (err) {
      console.error(`${label} error:`, err);
      showError(parseContractError(err));
      return false;
    } finally {
      setPending(null);
    }
  };

  return { read, write, router, runTx, pending, isOwner, isAdmin, refreshRole: checkRegistration, provider: readProvider };
};
